import {
  computed,
  inject,
  injectable,
  makeObservable,
  observable,
  singleton,
  watch,
  BaseModel,
} from '@wakeadmin/framework';
import type { IBay, MicroApp } from '@wakeadmin/mapp';
import type { I18nInstance } from '@wakeadmin/i18n';

import { BayRepo } from './BayRepo';
import { TreeContainer, RouteType, FindResult, TreeNode, extraOutsideUrl } from './tree';
import { PromiseQueue } from './base';
import { IBayModel } from './types';
import type { SessionInfo } from './session';
import { gotoChooseApp, gotoLogin, throwErrorIfNeedAfterSettled } from './utils';
import { AUTO_INDEX } from './constants';
import { pluginLoad } from './plugin';

declare global {
  interface DIMapper {
    'DI.bay.BayModel': BayModel;
  }

  interface EventMapper {
    /**
     * 基座启动
     */
    'Event.bay.setup': BayModel;

    /**
     * 基座启动异常
     */
    'Event.bay.setupError': Error;

    /**
     * 菜单变动
     */
    'Event.bay.menuChange': undefined;
  }
}

/**
 * 基座状态
 */
export enum BayStatus {
  INITIAL,
  PENDING,
  READY,
  ERROR,
}

/**
 * 是否启用预加载，默认关闭
 */
const ENABLE_PREFETCH: string = '[%= typeof preFetchEnabled !== "undefined" ? preFetchEnabled : "" %]';

/**
 * 菜单获取接口
 */
@injectable()
@singleton()
export class BayModel extends BaseModel implements IBayModel {
  @inject('DI.bay.BayRepo')
  repo!: BayRepo;

  @inject('DI.bay')
  bay!: IBay;

  @inject('DI.bay.i18n')
  i18n!: I18nInstance;

  /**
   * 基座状态
   */
  @observable.ref
  status: BayStatus = BayStatus.INITIAL;

  /**
   * 基座异常
   */
  @observable.ref
  error?: Error;

  /**
   * 菜单
   */
  @observable.ref
  menu?: TreeContainer;

  /**
   * 会话信息
   */
  @observable.ref
  sessionInfo?: SessionInfo;

  /**
   * 侧边栏折叠状态
   */
  @observable.ref
  sidebarCollasped = false;

  /**
   * 侧边栏可视状态
   */
  @observable.ref
  sidebarVisible = true;

  /**
   * 菜单(侧边栏+顶部导航栏)可视状态
   */
  @observable.ref
  menuVisible = true;

  /**
   * 菜单折叠动画效果
   */
  @observable.ref
  menuVisibleWithAnimation = false;

  /**
   * 微应用内容区的加载状态
   */
  @observable.ref
  mainLoading = false;

  private menuVisibleAnimationTimeout?: number;

  get loading() {
    return this.status !== BayStatus.READY && this.status !== BayStatus.ERROR;
  }

  @computed
  get location() {
    const { path, hashPath } = this.bay.location;
    return `${path}#${hashPath}`;
  }

  /**
   * 独立模式
   */
  @computed
  get standalone() {
    return 'bay-standalone' in this.bay.location.query || 'bay-standalone' in this.bay.location.hashQuery;
  }

  /**
   * 在后台菜单配置中定义了路由的微应用
   */
  @computed
  get appsInMenus(): Set<MicroApp> {
    const entries = this.menu?.entries;
    const apps = this.bay.apps;

    if (entries == null) {
      return new Set();
    }

    return new Set(
      apps.filter(i => {
        if (Array.isArray(i.activeRule)) {
          return i.activeRule.some(j => entries.has(j));
        } else {
          return entries.has(i.activeRule);
        }
      })
    );
  }

  @inject('DI.bay.promiseQueue')
  private setupQueue!: PromiseQueue<this>;

  /**
   * 可用的插件
   */
  private availablePlugins = window.__PLUGINS__ ?? {};
  private pluginQueues: Map<string, PromiseQueue<void>> = new Map();
  private pluginsLoaded: Set<string> = new Set();

  private initialized = false;

  constructor() {
    super();

    makeObservable(this);

    window.__MAPP_BAY_MODEL__ = this;

    // 支持菜单节点调试
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const me = this;
    Object.defineProperty(window, '$m', {
      enumerable: false,
      get() {
        // @ts-expect-error
        const currentSelectNode = window.$0 as HTMLElement | undefined;
        if (currentSelectNode != null) {
          const id = currentSelectNode?.dataset?.id;
          if (typeof id === 'string') {
            return me.menu?.findByPullIdentifierPath(id).result;
          }
        }

        return null;
      },
    });
  }

  /**
   * 等待启动, 只有基座正常启动之后才会 resolve。
   * 异常也不会抛出错误, 适用于要等待基座正常启动之后才进行的操作
   */
  async waitSetup(): Promise<this> {
    if (this.status !== BayStatus.READY) {
      // 推入队列中，
      return await this.setupQueue.push();
    }

    return this;
  }

  /**
   * 监听菜单变化
   * @param callback
   * @returns
   */
  watchMenuChange = (callback: () => void) => {
    return this.eventBus.on('Event.bay.menuChange', callback);
  };

  /**
   * 这个方法只能在主界面启动时调用
   */
  async initialize() {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    await this.singletonInitialize();
    await this.retryableInitialize();
  }

  /**
   * 重试
   */
  retry() {
    if (this.status !== BayStatus.ERROR) {
      console.warn(`[bay] 只有异常状态下，才能重试`);
      return;
    }

    this.retryableInitialize();
  }

  /**
   * 退出登录
   */
  logout = async () => {
    await this.repo.logout();
    gotoLogin();
  };

  getSidebarCollapsed = () => {
    return this.sidebarCollasped;
  };

  expandSidebar = () => {
    this.sidebarCollasped = false;
  };

  collapseSidebar = () => {
    this.sidebarCollasped = true;
  };

  /**
   * 切换侧边栏折叠状态
   */
  toggleSidebar = () => {
    this.sidebarCollasped = !this.sidebarCollasped;
  };

  showSidebar = (animate = true) => {
    if (this.sidebarVisible) {
      return;
    }

    this.setMenuVisibleAnimation(animate);
    this.sidebarVisible = true;
  };

  hideSidebar = (animate = true) => {
    if (!this.sidebarVisible) {
      return;
    }
    this.setMenuVisibleAnimation(animate);
    this.sidebarVisible = false;
  };

  /**
   * 显示菜单
   */
  showMenu = (animate = true) => {
    if (this.menuVisible) {
      return;
    }

    this.setMenuVisibleAnimation(animate);
    this.menuVisible = true;
  };

  /**
   * 隐藏菜单
   */
  hideMenu = (animate = true) => {
    if (!this.menuVisible) {
      return;
    }

    this.setMenuVisibleAnimation(animate);
    this.menuVisible = false;
  };

  /**
   * 显示内容区的加载动画
   */
  showMainLoading() {
    this.mainLoading = true;
  }

  /**
   * 隐藏内容区的加载动画
   */
  hideMainLoading() {
    this.mainLoading = false;
  }

  /**
   * @remarks
   * 返回的对象是一个**引用对象** 因此应该避免去**编辑该对象**
   *
   * 如果需要编辑 请手动复制一份
   *
   * {@inheritDoc TreeContainer.findByIdentifierPath}
   */
  findMenuByIdentifierPath(path: string): FindResult | undefined {
    return this.menu?.findByIdentifierPath(path);
  }

  /**
   * 根据微应用的名称打开。
   */
  openByAppName: IBayModel['openByAppName'] = (name, options) => {
    if (!this.assertOpen()) {
      return;
    }

    const app = this.bay.getApp(name);

    if (app == null) {
      console.warn(`[bay] openByAppName(${name}) 所指定的子应用未找到`);
      return;
    }

    if (!this.appsInMenus.has(app)) {
      console.warn(`[bay] openByAppName(${name}) 所指定的子应用未在菜单中定义`);
    }

    this.bay.openApp({ name, ...options });
  };

  /**
   *
   * 根据微应用的别名打开, 常用于多态应用。这里会优先打开在菜单中定义的微应用
   *
   * @param alias
   * @param options
   */
  openByAppAlias: IBayModel['openByAppAlias'] = (alias, options) => {
    const apps = this.bay.getAppByAlias(alias);

    if (apps.length === 0) {
      console.warn(`[bay] openByAppAlias(${alias}) 所指定的子应用未找到`);

      // 回退
      this.openByAppName(alias, options);
      return;
    }

    if (!this.assertOpen()) {
      return;
    }

    // 优先查找在菜单中定义的微应用
    const definedApps = apps.filter(i => this.appsInMenus.has(i));

    if (definedApps.length) {
      if (definedApps.length > 1) {
        console.warn(`[bay] openByAppAlias(${alias}) 所指定的子应用存在多个，出现歧义将打开第一个`, definedApps);
      }

      this.openByAppName(definedApps[0].name, options);
      return;
    }

    // 默认打开第一个
    this.openByAppName(apps[0].name, options);
  };

  /**
   * 打开主界面, 即菜单中定义的第一个根节点
   * @param options
   */
  openMain: IBayModel['openMain'] = options => {
    if (!this.assertOpen()) {
      return;
    }
    const first = this.menu?.topMenus?.[0];
    if (first == null) {
      console.warn(`[bay] openMain 菜单未配置`);
      return;
    }

    const node = !first.url ? first.children[0] : first;

    if (node == null) {
      console.warn(`[bay] openMain 菜单未配置`);
      return;
    }

    this.openTreeNode(node, options);
  };

  /**
   * 获取当前激活的节点
   * @returns
   */
  getActiveNode: IBayModel['getActiveNode'] = () => {
    return this.menu?.activeNode;
  };

  /**
   * 获取主入口 app
   * @returns
   */
  getMainApp = (): { app: MicroApp; matchedNode: TreeNode } | undefined => {
    if (!this.assertOpen()) {
      return;
    }

    const first = this.menu?.topMenus?.[0];
    if (first == null) {
      console.warn(`[bay] getMainApp 菜单未配置`);
      return;
    }

    const node = !first.url ? first.children[0] : first;

    if (node == null) {
      console.warn(`[bay] getMainApp 菜单未配置`);
      return;
    }

    if (!node.url) {
      console.warn(`[bay] getMain url 未配置`);
      return;
    }

    if (node.routeType !== RouteType.Href && node.routeType !== RouteType.None) {
      const app = this.bay.getAppByRoute(node.url);

      if (app) {
        // eslint-disable-next-line consistent-return
        return {
          app,
          matchedNode: node,
        };
      }
    }
  };

  /**
   * 根据权限标识符路径打开
   */
  openByIdentifierPath: IBayModel['openByIdentifierPath'] = (path, options) => {
    if (!this.assertOpen()) {
      return;
    }

    const result = this.menu?.findByIdentifierPath(path);
    if (result?.result != null) {
      this.openTreeNode(result.result, options);
    } else {
      console.warn(`[bay] openByIdentifierPath 未找到标识符路径为 ${path} 的节点`);
    }
  };

  redirectToSecondaryMenu: IBayModel['redirectToSecondaryMenu'] = options => {
    const autoFallback = options?.autoFallback ?? true;

    if (!this.assertOpen()) {
      return;
    }

    const menu = this.menu?.secondaryMenus.find(i => i.active);
    const fallback = () => {
      if (autoFallback) {
        console.debug(`[bay] redirectToSecondaryMenu 未找到激活的二级菜单，将回退到主界面`);
        const first = this.menu?.secondaryMenus[0];

        if (first) {
          this.openTreeNode(first, options);
        } else {
          this.openMain();
        }
      } else {
        console.warn(`[bay] redirectToSecondaryMenu 未找到激活的二级菜单`);
      }
    };

    if (menu) {
      const children = TreeContainer.filterMenu(menu.children);

      // 没有任何子节点、摒弃自己也没有 url
      if (children.length === 0 && !menu.url) {
        fallback();
        return;
      }

      // 尝试保留当前激活的三级菜单
      const child = children.find(i => i.active);
      if (child) {
        this.openTreeNode(child, options);
      } else {
        this.openTreeNode(menu, options);
      }
    } else {
      fallback();
    }
  };

  /**
   * 打开菜单节点
   */
  openTreeNode: IBayModel['openTreeNode'] = (node, options) => {
    if (!node.url) {
      console.warn(`[bay] 无法打开菜单节点，url 为空`, node);
      return;
    }

    switch (node.routeType) {
      case RouteType.Href: {
        // 外部链接, 直接打开
        window.open(node.url, node.identifier);
        break;
      }
      case RouteType.None:
        break;
      default: {
        // 首先判断是否是微应用, 如果是微应用, 则使用 history 打开
        const app = this.bay.getAppByRoute(node.url);
        if (app) {
          this.bay.openUrl({ path: node.url, ...options });
        } else {
          // 外部链接
          this.openOutside(node.url, options);
        }
      }
    }
  };

  /**
   * 打开外部链接
   */
  openOutside: IBayModel['openOutside'] = (url, options) => {
    const redirect = options?.redirect;
    if (redirect) {
      window.location.replace(url);
    } else {
      window.location.assign(url);
    }
  };

  /**
   * 打开链接
   * @param url
   */
  openUrl: IBayModel['openUrl'] = url => {
    this.bay.openUrl(url);
  };

  /**
   * 打开错误页面
   * @param args
   */
  openError: IBayModel['openError'] = (...args) => {
    this.bay.openError(...args);
  };

  /**
   * 生成落地页链接
   * @param props
   * @param addHost
   * @returns
   */
  generateLandingUrl: IBayModel['generateLandingUrl'] = (props, addHost) => {
    return this.bay.generateLandingUrl(props, addHost);
  };

  /**
   * 注册语言包
   * @param bundles
   */
  registerBundles: IBayModel['registerBundles'] = bundles => {
    return this.i18n.registerBundles(bundles);
  };

  /**
   * 设置语言
   * @param locale
   * @returns
   */
  setLocale: IBayModel['setLocale'] = locale => {
    return this.i18n.setLocale(locale);
  };

  /**
   * 获取语言
   * @returns
   */
  getLocale: IBayModel['getLocale'] = () => {
    return this.i18n.getLocale();
  };

  /**
   * 添加过滤器
   * @param filters
   */
  addExcludeAssetFilter: IBayModel['addExcludeAssetFilter'] = filters => {
    this.bay.addExcludeAssetFilter(filters);
  };

  /**
   * 加载插件
   * @param name
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  __dangerous_load_plugin: IBayModel['__dangerous_load_plugin'] = async (name: string): Promise<void> => {
    if (this.pluginsLoaded.has(name)) {
      return;
    }

    if (this.pluginQueues.has(name)) {
      // eslint-disable-next-line consistent-return
      return await this.pluginQueues.get(name)!.push();
    }

    const queue = new PromiseQueue<void>();
    this.pluginQueues.set(name, queue);

    try {
      if (!(name in this.availablePlugins)) {
        throw new Error(`[bay] 未找到插件 ${name}`);
      }

      await pluginLoad(this.availablePlugins[name]);

      queue.flushResolve();
    } catch (err) {
      queue.flushReject(err as Error);
      throw err;
    } finally {
      this.pluginsLoaded.add(name);
      this.pluginQueues.delete(name);
    }
  };

  // eslint-disable-next-line @typescript-eslint/naming-convention
  __dangerous_reset_menu_visible: IBayModel['__dangerous_reset_menu_visible'] = (root, toHidden) => {
    const dirty = this.menu?.resetVisible(root, toHidden);

    if (dirty) {
      this.emit('Event.bay.menuChange', undefined);
    }

    // 当前激活的节点已隐藏，自动跳转
    if (this.getActiveNode()?.isHidden) {
      this.redirectToSecondaryMenu();
    }

    return dirty;
  };

  private assertOpen(): boolean {
    if (this.menu == null) {
      console.warn(`[bay] openByIdentifierPath 需要等待基座启动后才能调用`);
      return false;
    }
    return true;
  }

  /**
   * 仅需执行一次
   */
  private singletonInitialize() {
    // 监听路由变动并匹配菜单
    watch(
      () => ({
        container: this.menu,
        location: this.location,
      }),
      ({ container, location }) => {
        if (container == null || location == null) {
          return;
        }

        console.debug('[bay] 开始匹配节点', location);

        if (location.includes('#/@/')) {
          const redirect = extraOutsideUrl(location);
          console.warn(`匹配到异常外挂路由: ${location}, 将重新重定向到: ${redirect}`);

          this.openUrl({ path: redirect, redirect: true });
          return;
        }

        const { result, exact } = container.findByRoute(location);

        if (result) {
          // 自动跳转到叶子节点
          if (AUTO_INDEX && exact) {
            const leaf = container.getFirstMenuLeaf(result);
            // 外部链接不太适合 auto_index
            if (leaf !== result && leaf.routeType !== RouteType.Href) {
              console.debug(`[bay] 自动 index 重定向到: ${leaf.url}`, leaf);
              this.openTreeNode(leaf, {
                redirect: true,
                // 透传参数
                hashQuery: this.bay.location.hashQuery,
              });
              return;
            }
          }

          console.debug(`[bay] 匹配(${exact ? '精确' : ''})到节点:`, result);
          container.lightUp(result, exact);
        }
      },
      { flush: 'pre' }
    );
  }

  /**
   * 可重试的初始化
   */
  private async retryableInitialize() {
    try {
      this.status = BayStatus.PENDING;
      this.error = undefined;

      const [userInfo, menu] = await Promise.allSettled([this.getSessionInfo(), this.createMenus()]);

      // 未选择应用
      if (userInfo.status === 'fulfilled' && !userInfo.value.appInfo) {
        return gotoChooseApp();
      }

      throwErrorIfNeedAfterSettled(userInfo, menu);

      this.status = BayStatus.READY;

      this.emit('Event.bay.setup', this);
      this.setupQueue.flushResolve(this);

      console.info(`[bay] 基座已启动`, this);

      // 开始预加载
      if (ENABLE_PREFETCH === 'true') {
        this.prefetch();
      }
    } catch (err) {
      console.error(`[bay] 基座启动异常`, err);
      this.status = BayStatus.ERROR;
      this.error = err as Error;
      this.emit('Event.bay.setupError', err as Error);
    }
  }

  private prefetch() {
    const list = Array.from(this.appsInMenus.values());
    if (list.length) {
      console.debug('[bay] 开始预加载微应用', list);
      this.bay.prefetch(list);
    }
  }

  /**
   * 获取会话信息
   * @returns
   */
  private getSessionInfo = async () => {
    return (this.sessionInfo = await this.repo.getSessionInfo());
  };

  /**
   * 配置菜单
   * TODO: 菜单配置缓存，以提升加载效率
   */
  private createMenus = async () => {
    const menus = await this.repo.getMenus();

    this.menu = new TreeContainer(menus);

    this.emit('Event.bay.menuChange', undefined);

    return this.menu;
  };

  private setMenuVisibleAnimation = (animate: boolean) => {
    this.cancelMenuVisibleAnimation();

    if (animate) {
      this.startMenuVisibleAnimation();
    }
  };

  private startMenuVisibleAnimation = () => {
    this.menuVisibleWithAnimation = true;
    this.menuVisibleAnimationTimeout = window.setTimeout(() => {
      this.menuVisibleWithAnimation = false;
    }, 1000);
  };

  private cancelMenuVisibleAnimation = () => {
    if (this.menuVisibleAnimationTimeout != null) {
      window.clearTimeout(this.menuVisibleAnimationTimeout);
      this.menuVisibleAnimationTimeout = undefined;
    }
  };

  /**
   * 查询当前员工身份
   */
  getCurrentEmployeeIdentityList = async () => {
    const res = await this.repo.getCurrentEmployeeIdentityList();
    return res?.map(({ orgId, orgName }) => ({
      value: orgId,
      label: orgName || '--',
    }));
  };

  /**
   * 切换身份
   */
  switchOrg = (identityId: number) => {
    return this.repo.switchOrg(identityId);
  };
}
